export const useGhgDataAssignment = (canEdit: boolean = true) => {
  const cid = useNuxtApp().$store.companies.companyId
  const loading = ref<boolean>(true)
  const assignedData = ref<DataAssignmentRecord[]>([])
  const pageNumber = ref<number>(1)
  const pageSize = ref<number>(15)
  const nextPage = ref<string | null>(null)
  const previousPage = ref<string | null>(null)
  const dataCount = ref<number>(0)
  const tableColumns = ref<EsgAssignmentItemNameFilter[]>([])
  const selectedPageToLoad = ref<number | null>(null)
  const allFilters = ref<AssignmentItemFilters[]>([])
  const assets = ref([])
  const groupedEsgMetricsData = ref<EsgMetricTheme[]>([])
  const categoryToScopeMapping = ref<Record<string, string>>({})
  const users = ref<UserData[]>([])
  const dataSortingParams = ref<DataAssignmentSortingParams[]>([])
  const ghgTopicMetricsData = ref([])
  const selectedAssignment = ref(null)
  const dataPointFrom = ref<number | null>(null)
  const dataPointTo = ref<number | null>(null)

  const showAddNewAssignmentModal = () => {
    useNuxtApp().$modal.show('assign-metrics')
  }

  const transformFetchedUsers = (data) => {
    return data.map((user) => {
      user.name = `${user.user?.first_name ?? ''} ${user.user?.last_name ?? ''}`
      user.id = user.user.id
      return user
    })
  }

  const formatAssignmentsData = (dataItems) => {
    return dataItems.map((log) => {
      log.asset_name = log.asset.asset_name
      log.metric_name = log.metric ? log.metric.name : (log.category ? log.category.name : '')
      log.datapoint = log.metric && !log.expected_datapoints ? 'N/A' : log.expected_datapoints
      log.theme = categoryToScopeMapping.value[log.metric_name]
      log.approver_list = log.approvers.map(person => ({
        ...person,
        full_name: `${person.first_name}`,
      })).sort((a, b) => a.first_name.trim().toLowerCase().localeCompare(b.first_name.trim().toLowerCase())).map(user => user.full_name).join(', ')

      log.owners = log.data_owners.map(person => ({
        ...person,
        full_name: `${person.first_name ?? ''}`,
      })).sort((a, b) => a.first_name.trim().toLowerCase().localeCompare(b.first_name.trim().toLowerCase())).map(user => user.full_name).join(', ')
      return log
    })
  }

  const paginationPrevAction = () => {
    if (previousPage.value) {
      const prevPageNum = new URL(previousPage.value).searchParams.get('page')
      if (prevPageNum) {
        pageNumber.value = parseInt(prevPageNum)
        loadAssigmentData(pageNumber.value)
      }
    }
  }

  const paginationNextAction = () => {
    if (nextPage.value) {
      const nextPageNum = new URL(nextPage.value).searchParams.get('page')
      if (nextPageNum) {
        pageNumber.value = parseInt(nextPageNum)
        loadAssigmentData(pageNumber.value)
      }
    }
  }

  const loadAssigmentData = (page: number | 1) => {
    loading.value = true
    selectedAssignment.value = null
    pageNumber.value = page
    const categoryIds = allFilters.value['metrics']?.filter(item => typeof item === 'number')

    const metricIds = allFilters.value['metrics']?.filter(item => typeof item === 'string')

    const queryParams = {
      page: page,
      page_size: pageSize.value,
      data_owners: allFilters.value['data_owners']?.join(','),
      data_collection_frequencies: allFilters.value['data_collection_frequencies']?.join(','),
      metrics: metricIds?.join(','),
      categories: categoryIds?.join(','),
      assets: allFilters.value['assets']?.join(','),
      approvers: allFilters.value['approvers']?.join(','),
      approval_types: allFilters.value['approval_types']?.join(','),
      ...dataSortingParams.value,
      from_datapoint: dataPointFrom.value,
      to_datapoint: dataPointTo.value,
    }
    useNuxtApp().$apis.esgMetrics.esgMetricAssetList(cid, queryParams).then(({ data: dataItems }) => {
      assignedData.value = formatAssignmentsData(dataItems.results)

      dataCount.value = dataItems.count
      nextPage.value = dataItems.next
      previousPage.value = dataItems.previous
      loading.value = false
    })
  }

  const handleItemNamesFilter = () => {
    const categoryIds = allFilters.value['metrics']?.filter(item => typeof item === 'number')

    const metricIds = allFilters.value['metrics']?.filter(item => typeof item === 'string')

    const queryParams = {
      data_owners: allFilters.value['data_owners']?.join(','),
      data_collection_frequencies: allFilters.value['data_collection_frequencies']?.join(','),
      metrics: metricIds?.join(','),
      assets: allFilters.value['assets']?.join(','),
      approvers: allFilters.value['approvers']?.join(','),
      categories: categoryIds?.join(','),
      approval_types: allFilters.value['approval_types']?.join(','),
    }

    useNuxtApp().$apis.esgMetrics.esgMetricFilters(cid, queryParams).then(({ data: namesData }) => {
      formatDataFilters(namesData)
    })
  }

  const handleDataFilters = (item) => {
    if (item.type == 'expected_datapoints' && item.value.length) {
      dataPointFrom.value = item.value[0]
      dataPointTo.value = item.value[1]
      Object.keys(dataSortingParams.value).forEach((key) => {
        dataSortingParams.value[key] = null
      })
    }
    else {
      allFilters.value[item.type] = item.value
    }
    loadAssigmentData(1)
    handleItemNamesFilter()
  }

  const handleDataSorting = (item) => {
    Object.keys(dataSortingParams.value).forEach((key) => {
      dataSortingParams.value[key] = null
    })
    dataSortingParams.value[item.type === 'data_collection_frequencies' ? 'sort_by_frequency' : `sort_by_${item.type.slice(0, -1)}`] = item.sort_name
    loadAssigmentData(1)
    loading.value = false
  }

  const formatDataFilters = (itemsData) => {
    tableColumns.value = [
      {
        title: 'Asset',
        filter: true, show_sort: true, sort_type: 'asc',
        type: 'assets',
        values: allFilters.value['assets'],
        items: itemsData.assets.map((asset) => {
          asset.id = asset.asset_id
          asset.name = asset.asset_name
          return asset
        }),
        number_sort: false,
      },
      {
        title: 'Category', filter: true, show_sort: false, sort_type: 'asc', type: 'metrics',
        values: allFilters.value['metrics'],
        items: itemsData.categories.map(category => ({
          id: category.category_id,
          name: category.category_name,
          type: 'category',
        })),
        number_sort: false,
      },
      {
        title: 'Approval Type',
        values: allFilters.value['approval_types'],
        filter: true, show_sort: true, sort_type: 'asc', type: 'approval_types', items: itemsData.approval_type.map((approval) => {
          approval.id = approval.type
          return approval
        }),
        number_sort: false,
      },
      {
        title: 'Data Owner',
        values: allFilters.value['data_owners'],
        filter: true, show_sort: false, sort_type: 'asc', type: 'data_owners', items: itemsData.data_owners.map((owner) => {
          owner.id = owner.data_owners__id
          owner.name = `${owner.first_name} ${owner?.last_name}`
          return owner
        }),
        number_sort: false,
      },
      {
        title: 'Approvers',
        values: allFilters.value['approvers'],
        filter: true, show_sort: false, sort_type: 'asc', type: 'approvers',
        items: itemsData.approvers.map((approver) => {
          approver.id = approver.approvers__id
          approver.name = `${approver.first_name} ${approver?.last_name}`
          return approver
        }),
        number_sort: false,
      },
      {
        title: 'Data Upload Frequency',
        filter: true, show_sort: true, sort_type: 'asc',
        values: allFilters.value['data_collection_frequencies'],
        type: 'data_collection_frequencies',
        items: itemsData.data_collection_frequencies.map((frequency) => {
          frequency.id = frequency.data_collection_frequency
          frequency.name = frequency.data_collection_frequency
          return frequency
        }),
        number_sort: false,
      },
      {
        title: 'Expected Data Points per Period',
        filter: true, show_sort: false, sort_type: 'asc', number_sort: true,
        values: [dataPointFrom.value, dataPointTo.value],
        type: 'expected_datapoints',
        items: [],
      },
    ]

    // Conditionally add Actions column if canEdit is true
    if (canEdit) {
      tableColumns.value.push({
        title: 'Actions',
        filter: false, show_sort: false, sort_type: 'asc',
        values: [],
        type: 'action',
        items: [],
        number_sort: false,
      })
    }

    loading.value = false
  }

  const downloadBulkImportTemplate = () => {
    useNuxtApp().$apis.esgMetrics.downloadBulkUserAssignmentImportTemplate(cid).then((response) => {
      const filename = 'Veridis - User Assignment Bulk Import Template.xlsm'
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    }).catch((error) => {
      console.error('Failed to download the bulk import template:', error)
      useNuxtApp().$toast.error('Failed to download the bulk import template. Please try again later.')
    })
  }

  const getUsersList = async (page: number = 1, searchName: string = '') => {
    const { getUsers } = useNuxtApp().$apis.accounts

    let queryParams: ManageUsersQueryParams = {
      page,
      page_size: 50,
      status: ['ACTIVE', 'PENDING'],
      role: ['ADMIN', 'CONTRIBUTOR_ALL_ORG_DATA', 'CONTRIBUTOR_ASSIGNED_DATA'],
      sorted_by_name: 'asc',
    }

    if (searchName.length) {
      queryParams = {
        ...queryParams,
        query_name: searchName,
      }
    }

    const { data } = await getUsers(queryParams)

    return { data: data.results, next: data.next !== null }
  }

  const initializeData = async () => {
    const { isManageUsersEnabled } = useFeatureFlag()

    const { esgMetricAssetList, esgMetricFilters, groupedEsgMetrics } = useNuxtApp().$apis.esgMetrics
    const { listAll } = useNuxtApp().$apis.assets
    const { ownersApproversList } = useNuxtApp().$apis.companies
    const { scopesSortedCategories } = useNuxtApp().$apis.emissionTracker

    const [
      { data: dataOwnerCategories },
      { data: listOfItems },
      { data: assetData },
      { data: metricsData },
      { data: usersData },
      { data: ghgMetricsData },
    ] = await Promise.all([
      esgMetricAssetList(cid, {
        page: 1,
        page_size: 15,
        sort_by_metric: 'asc',
        sort_by_asset: 'asc',
        sort_by_frequency: 'asc',
        sort_by_approval_type: 'asc',
      }),
      esgMetricFilters(cid, {
        data_collection_frequencies: '',
      }),
      listAll(),
      groupedEsgMetrics(cid),
      isManageUsersEnabled() ? { data: [] } : ownersApproversList(cid),
      scopesSortedCategories(),
    ])

    ghgMetricsData.forEach((scope) => {
      scope.categories.forEach((category) => {
        categoryToScopeMapping.value[category.name] = `Scope ${scope.id}`
      })
    })

    assignedData.value = formatAssignmentsData(dataOwnerCategories.results)
    dataCount.value = dataOwnerCategories.count
    nextPage.value = dataOwnerCategories.next
    previousPage.value = dataOwnerCategories.previous
    pageNumber.value = 1
    groupedEsgMetricsData.value = metricsData
    assets.value = assetData
    users.value = isManageUsersEnabled() ? usersData : transformFetchedUsers(usersData)
    ghgTopicMetricsData.value = [{ theme: 'GHG', topics: ghgMetricsData }]
    formatDataFilters(listOfItems)
  }

  return {
    loading,
    assignedData,
    pageNumber,
    pageSize,
    nextPage,
    previousPage,
    dataCount,
    tableColumns,
    selectedPageToLoad,
    allFilters,
    assets,
    groupedEsgMetricsData,
    categoryToScopeMapping,
    users,
    dataSortingParams,
    ghgTopicMetricsData,
    selectedAssignment,
    dataPointFrom,
    dataPointTo,
    showAddNewAssignmentModal,
    getUsersList,
    transformFetchedUsers,
    formatAssignmentsData,
    paginationPrevAction,
    paginationNextAction,
    loadAssigmentData,
    handleItemNamesFilter,
    handleDataFilters,
    handleDataSorting,
    formatDataFilters,
    downloadBulkImportTemplate,
    initializeData,
  }
}
